/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { DomainToken } from "@tivocorp/data-domain-lib";
import {
  DomainTokensService,
  TsriServiceEndpointService,
  Storage
} from "@tivocorp/data-domain-lib";

import { ISubscription } from "../../ISubscription";
import { SubscriptionModel } from "../../SubscriptionModel";
import { MILLISECONDS_IN_A_SECOND } from "../../../../config";
import { DeviceModel } from "../../../device";
import { TokenManager } from "../../../auth/TokenManager";
import { SDTState } from "../../enums";
import { SDTInfo } from "../../interfaces/index";
import { TScopedDomainTokenNotifier } from "../../types";
import {
  Diagnostics,
  Log,
  PostSigninApi,
  PostSigninLogger
} from "../../../logging";
import { DiagnosticSeverity } from "../../../types";
import { ApplicationFeatureArea } from "../../../enums";

export class ScopedDomainTokenModel {
  private static readonly TAG: string = "ScopedDomainTokenModel";

  private static oScopedDomainTokenModel: ScopedDomainTokenModel | null = null;

  /**
   * A stored token with less than this left is fetched afresh rather than
   * reused: license renewals made with it during a long session would
   * otherwise start failing the moment it expires.
   */
  private static readonly SDT_MIN_REMAINING_MS: number = 5 * 60 * 1000;

  private oITivoSubscription: ISubscription<TScopedDomainTokenNotifier>;

  private constructor() {
    this.oITivoSubscription =
      new SubscriptionModel<TScopedDomainTokenNotifier>();
  }

  private async _commonParameters() {
    return {
      applicationFeatureArea: ApplicationFeatureArea.ALL,
      applicationName: DeviceModel.getAppName(),
      applicationVersion: DeviceModel.getAppVersionName(),
      bodyId: await DeviceModel.getTiVoSettings().getBodyId(),
      deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
      productName: DeviceModel.getAppName(),
      acceptLanguage: DeviceModel.getTVProperties().getDateLocale() || "en-US"
    };
  }

  private async _commonParametersWithTimeZone() {
    return {
      ...(await this._commonParameters()),
      timeZone: DeviceModel.getTVProperties().getDefaultTimeZone()
    };
  }

  private log(
    level: "debug" | "info" | "warn" | "error",
    message: string,
    ...optionalParams: unknown[]
  ): void {
    Diagnostics[level](ScopedDomainTokenModel.TAG, message, ...optionalParams);
    Log[level[0] as "d" | "i" | "w" | "e"](
      ScopedDomainTokenModel.TAG,
      message,
      ...optionalParams
    );
    if (level === "error") {
      Diagnostics.error(
        ScopedDomainTokenModel.TAG,
        "stacktrace:,",
        optionalParams[0]
      );
    }
  }

  // returns the new calculated expiration time which is 90% to 95% of original time

  private sdtRefreshTime(domainExpirationTime: number): number {
    // generates number between 5 to 10 (inclusive)
    // eslint-disable-next-line sonarjs/pseudo-random
    const expirationRandomFactor = Math.floor(Math.random() * 6) + 5;

    const expirationTimeInSeconds =
      domainExpirationTime * MILLISECONDS_IN_A_SECOND;

    /* Calculating number of hours more than current time and then.
     * Calculate expirationRandomFactor (range 5 to 10) percentage of expiration time.
     */
    const newDomainExpirationTime =
      (expirationTimeInSeconds - Date.now()) * (expirationRandomFactor / 100);
    return expirationTimeInSeconds - newDomainExpirationTime;
  }

  public static getInstance(): ScopedDomainTokenModel {
    if (!this.oScopedDomainTokenModel) {
      this.oScopedDomainTokenModel = new ScopedDomainTokenModel();
    }

    return this.oScopedDomainTokenModel;
  }

  public async getTokenFromStore(
    drmCustomDomain: string,
    drmCustomHeaderAuthScheme: string
  ): Promise<DomainToken | null> {
    const domainTokenKey = `${drmCustomDomain}#${drmCustomHeaderAuthScheme}`;
    let isTokenAvailableInStore: boolean = false;
    const scopedDomainTokenData: Record<string, DomainToken> | null =
      await Storage.getInstance().get(Storage.Keys.SCOPE_DOMAIN_TOKENS);

    if (scopedDomainTokenData !== null) {
      isTokenAvailableInStore = Object.keys(scopedDomainTokenData).includes(
        domainTokenKey
      );
    }

    return isTokenAvailableInStore
      ? scopedDomainTokenData && scopedDomainTokenData[domainTokenKey]
      : null;
  }

  public async getToken(
    drmCustomDomain: string,
    drmCustomHeaderAuthScheme: string,
    isForced?: boolean,
    isRefreshed: boolean = false
  ): Promise<DomainToken> {
    const scopedDomainTokenNotifyObject: TScopedDomainTokenNotifier = {
      sdtState: SDTState.SUCCESS,
      drmCustomDomain,
      drmCustomHeaderAuthScheme
    };
    const domainTokenfromStorage = await this.getTokenFromStore(
      drmCustomDomain,
      drmCustomHeaderAuthScheme
    );
    const currentEpochTime = Date.now();
    // Compared against a fixed margin, not sdtRefreshTime(): that scales the
    // time still remaining, so `sdtRefreshTime(exp) > now` reduces to
    // `exp > now` and a token counted as valid until the moment it expired.
    const isScopeDomainTokenDataValid = !!(
      domainTokenfromStorage &&
      domainTokenfromStorage.expiration * MILLISECONDS_IN_A_SECOND -
        currentEpochTime >
        ScopedDomainTokenModel.SDT_MIN_REMAINING_MS
    );

    let response: DomainToken | undefined;
    if (isForced || !isScopeDomainTokenDataValid) {
      const domainTokenServiceParams = {
        ...(await this._commonParametersWithTimeZone()),
        basePath:
          TsriServiceEndpointService.getServiceEndpointByServiceName(
            "domain-tokens"
          )
      };
      // Through TokenManager, not raw storage: a scoped domain token is minted
      // against the account token, so reading an expired one here failed DRM
      // licence acquisition rather than refreshing.
      const accessToken: string = await TokenManager.getValidAccessToken();
      const scopedDomainTokenParams = {
        authorization: `${drmCustomHeaderAuthScheme}`,
        domain: drmCustomDomain,
        accessToken: accessToken
      };
      const startTimeMs: number = Date.now();
      try {
        response = await new DomainTokensService(
          domainTokenServiceParams
        ).fetchScopedDomainToken(scopedDomainTokenParams);
        PostSigninLogger.logApiSuccess(
          PostSigninApi.ScopedDomainToken,
          Date.now() - startTimeMs
        );
      } catch (error: unknown) {
        PostSigninLogger.logApiFailure(
          PostSigninApi.ScopedDomainToken,
          error,
          DiagnosticSeverity.Error,
          Date.now() - startTimeMs
        );
        this.log(
          "error",
          `Failed to fetch scope domain tokens for: ${drmCustomDomain}`
        );
        scopedDomainTokenNotifyObject.sdtState = SDTState.ERROR;
        this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch domain tokens";
        throw new Error(errorMessage);
      }
    }

    const domainToken =
      isForced || !isScopeDomainTokenDataValid
        ? response
        : domainTokenfromStorage;

    if (!domainToken || !domainToken.token || !domainToken.expiration) {
      this.log("error", `scope domain data missing for: ${drmCustomDomain}`);
      scopedDomainTokenNotifyObject.sdtState = SDTState.ERROR;
      this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
      throw new Error("Scope Domain Token missing");
    }
    scopedDomainTokenNotifyObject.token = domainToken.token;
    if (isRefreshed) {
      this.log("info", `${drmCustomDomain} scope domain token refreshed`);
      scopedDomainTokenNotifyObject.sdtState = SDTState.REFRESH;
      this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
    } else {
      this.log(
        "info",
        `${drmCustomDomain} scope domain token fetched successfully`
      );
      this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
    }

    return domainToken;
  }

  public getSDTFetchListener(): ISubscription<TScopedDomainTokenNotifier> {
    return this.oITivoSubscription;
  }

  public async getTokenToBeRefreshed(): Promise<SDTInfo | null> {
    const scopedDomainTokenData: Record<string, DomainToken> | null =
      await Storage.getInstance().get(Storage.Keys.SCOPE_DOMAIN_TOKENS);

    if (!scopedDomainTokenData) return null;

    let lowestExpirationTime: number = Infinity;
    let domainTokenKey: string = "";
    Object.keys(scopedDomainTokenData).forEach((domain) => {
      if (scopedDomainTokenData[domain].expiration < lowestExpirationTime) {
        lowestExpirationTime = scopedDomainTokenData[domain].expiration;
        domainTokenKey = domain;
      }
    });
    let refreshTime: number = this.sdtRefreshTime(lowestExpirationTime);

    // Calculate delay from current time to refresh time
    const currentTime = Date.now();
    refreshTime = Math.max(refreshTime - currentTime, 0);
    /*
     ** This will extract drmCustomDomain and drmCustomHeaderAuthScheme
     ** from domainTokenKey (drmCustomDomain#drmCustomHeaderAuthScheme)
     */
    const domainAndAuthScheme = domainTokenKey.split("#");
    return {
      drmCustomDomain: domainAndAuthScheme[0],
      drmCustomHeaderAuthScheme: domainAndAuthScheme[1],
      refreshTime
    } as SDTInfo;
  }
}
